import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import cx from 'classnames';
import { uniqueId } from 'lodash';

import { I18n } from 'common/i18n';
import { DragHandleIcon } from './DragHandleIcon';
import { DragDropElementWrapper } from './index';
import { getSortableStyle } from './sortableStyles';

const scope = 'common';
interface SortableConditionElementProps<T = unknown> {
  item: DragDropElementWrapper<T>;
  isDragOverlay?: boolean;
  iconName?: string;
}

export const SortableConditionElement = <T = unknown,>({
  item,
  isDragOverlay = false,
  iconName = 'drag_handle',
}: SortableConditionElementProps<T>) => {
  // Generate a stable default ID if none provided
  const sortableId = useMemo(() => item.id || uniqueId('sortable-condition-'), [item.id]);
  const ariaLabel = I18n.t('drag_to_reorder_aria_label', { scope });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: {
      type: 'sortable-item',
      item: item,
    },
  });

  const style = getSortableStyle({
    transform,
    transition,
    isDragging,
    isDragOverlay,
    additionalStyles: {
      padding: isDragOverlay ? '10px' : '',
    },
  });

  return (
    <div
      {...item.additionalItemProps}
      className={cx('drag-element', 'conditional-config', item.className, { 'is-dragging': isDragging })}
      ref={setNodeRef}
      style={style}
    >
      <div>
        <div
          className={cx('drag-handle', 'conditional-config-header')}
          {...attributes}
          {...listeners}
          aria-label={ariaLabel}
        >
          <DragHandleIcon name={iconName} slot="leading" isDraggable={item.dragAndDroppable} />
          {item.element}
        </div>
      </div>
      <div>{item.mainElement}</div>
    </div>
  );
};
